import { CadendarConsts, DDNSchema, E164Schema, Patients } from "cadendar-shared";
import { checkers, formaters } from "vincent-utils";

const { etatCivilStrings } = CadendarConsts;

type keys = keyof typeof etatCivilStrings;

type TempInput = Omit<{ [K in keys]: string }, "GENRE"> & {
  GENRE: "M" | "F" | null;
};

export interface ReminderConsentInput {
  email_consent: boolean | null;
  sms_consent: boolean | null;
}

export const validate = (
  values: Omit<Record<keys, string>, "GENRE"> & { GENRE: "M" | "F" | null },
) => {
  const errors = {} as Record<keys, string>;

  if (!values.NAME) {
    errors.NAME = "obligatoire";
  } else if (!checkers.isNomOrPrenom(values.NAME)) {
    errors.NAME = "nom invalide";
  }

  if (!values.FORENAME) {
    errors.FORENAME = "obligatoire";
  } else if (!checkers.isNomOrPrenom(values.FORENAME)) {
    errors.FORENAME = "prénom invalide";
  }

  if (!values.BIRTHDATE) {
    errors.BIRTHDATE = "obligatoire";
  } else if (!checkers.isValidDdn(values.BIRTHDATE)) {
    errors.BIRTHDATE = "ddn invalide";
  }

  if (
    values.PORTABLEE164 &&
    !E164Schema.safeParse(values.PORTABLEE164).success
  ) {
    errors.PORTABLEE164 = "numéro de portable invalide";
  }

  return errors;
};

export const converters = {
  BIRTHDATE: function (ddnString: string) {
    "use strict";
    return formaters.formatDDNForStoring(ddnString);
  },
  NAME: function (nomString: string) {
    "use strict";
    return formaters.capitaliseEveryFirstLetter(nomString);
  },
  FORENAME: function (nomString: string) {
    "use strict";
    return formaters.capitaliseEveryFirstLetter(nomString);
  },
  MAIDEN_NAME: function (nomString: string) {
    "use strict";
    return formaters.capitaliseEveryFirstLetter(nomString);
  },
  PORTABLEE164: function (portable: string) {
    "use strict";
    return formaters.formatTelNumberForDisplay(portable);
  },
  EMAIL: (value: string) => value,
  COMMENTS: (value: string) => value,
};

export const getDefaultReminderConsentInput = (): ReminderConsentInput => ({
  email_consent: true,
  sms_consent: true,
});

export const patientToReminderConsentInput = (
  patient?: Partial<Pick<Patients, "email_consent" | "sms_consent">>,
) => ({
  email_consent: patient?.email_consent ?? null,
  sms_consent: patient?.sms_consent ?? null,
});

export const consentInputToCheckedValue = (value: boolean | null) =>
  value === true;

export const getPatientDTOFromTempInput = (
  tempInput: TempInput,
  reminderConsentInput: ReminderConsentInput,
) => {
  const portable164Parsed = E164Schema.safeParse(tempInput.PORTABLEE164);
  const portableE164 = portable164Parsed.success
    ? portable164Parsed.data
    : null;
  const ddn = DDNSchema.parse(tempInput.BIRTHDATE);

  const dto = {
    name: tempInput.NAME.toLowerCase(),
    forename: tempInput.FORENAME.toLowerCase(),
    birthdate: ddn,
    portablee164: portableE164,
    email: tempInput.EMAIL || "",
    email_consent: reminderConsentInput.email_consent,
    sms_consent: reminderConsentInput.sms_consent,
    comments: tempInput.COMMENTS || "",
    maiden_name: tempInput.MAIDEN_NAME.toLocaleLowerCase() || null,
    patient_flags: null,
    famille_id: null,
    adr_flag: null,
    adr_id: null,
    raf: null,
    seen: null,
    former_patient: false,
    adr_other_text: null,
    genre: tempInput.GENRE,
  };

  return dto;
};

export const patientToTempInput = (
  patient:
    | Partial<
        Pick<
          Patients,
          | "name"
          | "forename"
          | "birthdate"
          | "portablee164"
          | "email"
          | "comments"
          | "genre"
          | "maiden_name"
        >
      >
    | {
        name: string;
        forename: string;
        birthdate: string;
        portablee164: string;
        email: string;
        comments: string;
        genre: "M" | "F" | null;
        maiden_name?: string | null;
      },
) => {
  return {
    NAME: (patient.name && converters.NAME(patient.name)) || "",
    FORENAME: (patient.forename && converters.FORENAME(patient.forename)) || "",
    BIRTHDATE:
      (patient.birthdate && converters.BIRTHDATE(patient.birthdate)) || "",
    PORTABLEE164:
      ("portablee164" in patient &&
        patient.portablee164 &&
        converters.PORTABLEE164(patient.portablee164)) ||
      "",
    EMAIL: (patient.email && converters.EMAIL(patient.email)) || "",
    COMMENTS: patient.comments || "",
    MAIDEN_NAME:
      (patient.maiden_name && converters.MAIDEN_NAME(patient.maiden_name)) ||
      "",
    GENRE: patient.genre || null,
  };
};

export const validatorMap = {
  NAME: (value: string) => {
    return checkers.isNomOrPrenom(value);
  },
  FORENAME: (value: string) => {
    return checkers.isNomOrPrenom(value);
  },
  MAIDEN_NAME: (value: string) => {
    return value === "" || checkers.isNomOrPrenom(value);
  },
  BIRTHDATE: (value: string) => {
    return checkers.isValidDdn(value);
  },
  PORTABLEE164: (value: string) => {
    return !!checkers.isPortable(value);
  },
  EMAIL: (value: string) => {
    return !!checkers.isEmail(value);
  },
  COMMENTS: (value: string) => {
    return value.length > 0;
  },
  GENRE: (value: "M" | "F" | null) => {
    return value === "M" || value === "F" || value === null;
  },
};
